import { z } from 'zod';
import { zRegisterClosureType } from './enums';
import { zPrice } from './primitives';

export const registerClosureSchema = z.object({
  /**
   * How the session was closed. UNATTENDED closures are recorded by the primary POS (idle outside
   * service hours, or catch-up on launch) with nobody counting the till, so their actuals are null
   * until a restaurant user enters them in admin-web. Entering actuals does NOT change the type —
   * it records how the day was closed. Manual closures stay immutable. The trading-day backfill
   * also records one unattended closure (no terminal) per past day staff never closed.
   */
  type: zRegisterClosureType.default('MANUAL'),

  // Actual (and their diffs) are null on unattended closures until entered later in admin-web.
  cashDiff: z.number().nullable(), // Allow for negatives
  cashExpected: z.number(),
  cashActual: z.number().nullable(),

  eftposExpected: z.number(),
  eftposActual: z.number().nullable(),
  eftposDiff: z.number().nullable(), // Allow for negatives

  /** `closedAt` (set locally) allows for offline support in the future. Also allows importing past EOD reports. */
  closedAt: z.number(),
  /** IdForVendor may change if the iOS POS app is uninstalled and reinstalled. `posTerminalUuid` is more likey to be stable reference. */
  posTerminalUuid: z.string().nullable().default(null),
  /**
   * Session window: previous register closure → `closedAt`.
   * - `ordersCreatedBefore` equals `closedAt`, the inclusive creation-time cutoff for archiving.
   *   Earlier sessions' late arrivals are included (see `queryUnsettledClosedOrders`).
   * - `ordersCreatedAfter` is informational; it does not limit archiving. The server computes it
   *   via `registerClosures/domain.getSessionStart` because the POS's value becomes stale if a
   *   super admin deletes the latest register closure.
   */
  ordersCreatedAfter: z.number(),
  ordersCreatedBefore: z.number(),

  /**
   * `creditCardActual` is the sum of all order payments where payment type is 'CREDIT_CARD'.
   * This value should match merchant provider records (eg Stripe).
   **/
  creditCardActual: z.number(),
  /**
   * X/Z report categorySalesRow shows total for the orderItem.
   * This line total includes "order item" level discounts/surcharges.
   * If each orderItem row total is added together, then should match the total of payments received.
   * sum of all order item rows = cash expected + eftpos expected + credit card actual + orderSurchargesTotal - orderDiscountsTotal
   * These discount are "order" level discount/surcharges only, and do not include "order item" discounts/surcharges.
   * These fields are nullable because we have not always calculated these values. To use 0 would be misleading if the values were not calculated.
   **/
  // TODO: These fields are not related to payment reconsiliation / register closures.
  // orderDiscountsTotal: z.number().nullable().default(null),
  // orderSurchargesTotal: z.number().nullable().default(null),

  // TODO: Potentially add in later...
  // cashMovements: zPrice,
  // cashRefunds: zPrice,
  // cashTakings: zPrice,
  // eftposRefunds: zPrice,
  // posUserUuid: z.string().nullable().default(null),

  restaurantId: z.string().optional(),
  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type RegisterClosure = z.output<typeof registerClosureSchema>;

/** Strict schema */
export const registerClosureStrictSchema = registerClosureSchema.extend({
  cashDiff: z.int().nullable(),
  cashExpected: zPrice,
  cashActual: zPrice.nullable(),
  eftposExpected: zPrice,
  eftposActual: zPrice.nullable(),
  eftposDiff: z.int().nullable(),
  creditCardActual: zPrice,
});

/** Convex schema */
export const registerClosureCvxSchema = registerClosureSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  restaurantId: z.string(),
});
export type RegisterClosureCvx = z.output<typeof registerClosureCvxSchema>;

/**
 * Request queued by the primary POS for `posNative.closeRegisterUnattended`.
 * The server sets all other fields: totals come from orders eligible for archiving, and
 * cash/EFTPOS actuals stay null until staff enter them in admin-web.
 */
export const registerClosureRequestSchema = z.object({
  closedAt: z.number(),
  ordersCreatedAfter: z.number(),
  posTerminalUuid: z.string().nullable(),
});
export type RegisterClosureRequest = z.output<typeof registerClosureRequestSchema>;
